if (typeof navigator === "undefined") {
  navigator = { userAgent: "" } as any;
}

import "./HomePage.css";
import { Suspense, useRef, useEffect } from "react";
import { HeroSection } from "@tennr/frontend/sections/HeroSection";
import { HowSection } from "@tennr/frontend/sections/HowSection";
import { PartnersSection } from "@tennr/frontend/sections/PartnersSection";
import { FeatureListSection } from "@tennr/frontend/sections/FeatureListSection";
import { TestimonialsSection } from "@tennr/frontend/sections/TestimonialsSection";
import { DeepDiveSection } from "@tennr/frontend/sections/DeepDiveSection";
import { CaseStudiesSection } from "@tennr/frontend/sections/CaseStudiesSection";
import { PressSection } from "@tennr/frontend/sections/PressSection";
import { PageBackground } from "@tennr/frontend/components/PageBackground";
import { SiteFooter } from "@tennr/frontend/components/SiteFooter";
import { MetadataTags } from "@tennr/frontend/components/MetadataTags";
import { Heading } from "@tennr/frontend/components/Heading";
import { LottieAnimation } from "@tennr/frontend/components/LottieAnimation";
import { useMobile } from "@tennr/frontend/hooks/useMobile";
import { Button } from "@tennr/frontend/components/Button";
import { Link } from "@tanstack/react-router";
import { doubleQuote, singleQuote } from "@tennr/frontend/utils";

const bgImage = "/img/homepage-hero-bg.jpg";
const bgImageVert = "/img/homepage-hero-bg-vert2.jpg";

export const HomePage = ({ cmsContent }) => {
  const isMobile = useMobile();

  // create the tagline ref for the footer to use
  const taglineCardRef = useRef<HTMLDivElement>(null!);

  useEffect(() => {
    const preload = (src?: string) => {
      if (!src) return;
      const img = new Image();
      img.src = src;
    };

    const isMobile = window.innerWidth <= 768 || window.matchMedia("(orientation: portrait)").matches;

    if (isMobile) {
      // Preload vertical/mobile images
      preload("/img/homepage-hero-bg-vert2.jpg");
      preload("/img/product-hero-bg-vert2.jpg");
      preload("/img/customer-stories-hero-bg-vert.jpg");
      preload("/img/about-us-hero-bg-vert.jpg");
      preload("/img/case-study-hero-bg-vert.jpg");
      preload("/img/book-demo-hero-bg-vert.jpg");
    } else {
      // Preload desktop images
      preload("/img/homepage-hero-bg.jpg");
      preload("/img/product-hero-bg.jpg");
      preload("/img/customer-stories-hero-bg.jpg");
      preload("/img/about-us-hero-bg.jpg");
      preload("/img/case-study-hero-bg.jpg");
      preload("/img/book-demo-hero-bg.jpg");
    }

    preload("/img/grain.png"); // always used
  }, []);

  // main application with all sections
  return (
    <>
      <MetadataTags
        title="Tennr"
        description="Ensure payor-ready documentation, boost conversions, and cut first‑pass denials with Tennr."
        imageUrl="https://tennr.com/share_cards/sharecard-home.webp"
      />
      <Suspense fallback={<div className="loading">Loading...</div>}>
        <main className="home-page">
          <PageBackground bgImage={bgImage} bgImageVert={bgImageVert} />
          <HeroSection noAnimate={true} className="bottom-left">
            <p>
              Ensure payor-ready documentation, boost conversions, and cut denials. Tennr takes you from fax to first
              visit, faster.
            </p>
            <Link to="/book-a-demo">
              <Button
                text="FIX THE FRONT OFFICE"
                width={254}
                dynamicContrast={true}
                altBackground={"#EEDFD1CC"}
                altTextColor={"#2B2D32"}
                background={"#9B4E45"}
                textFromColor={"#EEDFD1"}
                className="hero-cta-button"
                dontAnimateIntro={true}
              />
            </Link>
          </HeroSection>
          <div className="hero-bottom-copy">
            <Heading
              title={cmsContent?.bottomSection?.heading?.title || "UNDER THE HOOD"}
              heading={
                cmsContent?.bottomSection?.heading?.heading ||
                (isMobile ? "Tennr is powered \nby RaeLM™" : "Tennr is powered by RaeLM™")
              }
              position="left"
              mobilePosition="center"
              verticalAlign="center"
              color="dynamic"
              variant="section"
              body={
                cmsContent?.bottomSection?.heading?.body ||
                "The only vision-language model trained to interpret unstructured medical records against complex payor criteria."
              }
              width={600}
            ></Heading>
            {/* <HeroAnimation /> */}
            {/* TODO: need to refactor our LottieAnimation component to not have to set the className on both the container and the lottie itself.  Removing it from the child will crop this lottie. see: e7d4d7fa9 for breaking change  */}
            <LottieAnimation
              src={
                cmsContent?.bottomSection?.media?.src || cmsContent?.backgroundMedia?.src || "/lottie/HP_realm.lottie"
              }
              loop={true}
              autoplay={true}
              className="hero-lottie"
            />
          </div>

          <HowSection />
          <PartnersSection />
          <FeatureListSection />
          <TestimonialsSection
            testimonials={[
              {
                quote: doubleQuote(
                  "With Tennr, we’re able to do things so much faster, and it's just huge. It’s been a game changer. We’re able to reach out to patients so much faster."
                ),
                image: "/img/testimonials/alyssa-jakubowski.jpeg",
                attributedTo: "Alyssa Jakubowski",
                attributedToTitle: "Revenue Cycle and Compliance Manager at NEB Medical",
              },
              {
                quote: doubleQuote(
                  `I call it the ${singleQuote("Tennr effect.")} For every action, there's a reaction. As we speed things up at the front of the time, it's going to have downstream effects.`
                ),
                image: "/img/testimonials/chip-holland.jpeg",
                attributedTo: "Chip Holland",
                attributedToTitle: "President of Total Sleep Management",
              },
              {
                quote: doubleQuote("We hit 260 faxes in one day… and were able to manage it."),
                image: "/img/testimonials/amy-willis.jpeg",
                attributedTo: "Amy Willis",
                attributedToTitle: "Director of Complex Rehab at King Drug",
              },
            ]}
          />
          <DeepDiveSection
            images={false}
            lottie={true}
            button={true}
            items={[
              {
                title: "Classify",
                body: "Documents are labeled, triaged, and routed correctly. Tennr splits multiple patient packets and joins duplicates to send everything to the right place, down to the page and patient level.",
                id: "classify",
              },
              {
                title: "Intake",
                body: "Replace manual data entry with automation that extracts information from any document format and populates your EHR without human intervention.",
                id: "intake",
              },
              {
                title: "Request Missing Information",
                body: "Automatically fax or call to request what's missing. Don't make your team search, just have them double check that the right info is on its way.",
                id: "request",
              },
              {
                title: "Determine Active Coverage",
                body: "Select the right payor and plan every time to properly determine active coverage with an under-the-hood payor mapping engine.",
                id: "determine",
              },
              {
                title: "Run Benefit Investigations & Submit Prior Authorizations",
                body: "Your team spending hours trying to obtain a successful prior authorization are over. We've got you covered from portal to phone tree.",
                id: "run",
              },
              {
                title: "Human-In-The-Loop Approvals",
                body: "Your team spending hours trying to obtain a successful prior authorization are over. We've got you covered from portal to phone tree.",
                id: "human",
              },
            ]}
          />
          <CaseStudiesSection
            onRefReady={(ref) => {
              // copy the ref to our local ref
              taglineCardRef.current = ref.current;
            }}
          />
          <PressSection />
        </main>
        <SiteFooter bgImage="/img/footer-bg.jpg" bgImageVert="/img/footer-bg-vert.jpg" />
      </Suspense>
    </>
  );
};
